import React, { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes } from 'react';

/* ───────── Shared base style ───────── */
const baseInputClass =
  'w-full bg-[var(--sage-bg)] border border-[var(--sage-white-10)] rounded-lg text-[var(--sage-text)] placeholder-[color-mix(in_srgb,var(--sage-text-muted)_60%,transparent)] outline-none transition-colors focus:border-[var(--sage-primary)]';

/* ───────── TextInput ───────── */
export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  ({ label, hint, className = '', ...rest }, ref) => (
    <div className="space-y-1">
      {label && (
        <label className="block text-[10px] font-bold text-[var(--sage-text-muted)] uppercase tracking-[0.15em]">
          {label}
        </label>
      )}
      <input
        ref={ref}
        className={`${baseInputClass} p-2.5 text-sm ${className}`}
        {...rest}
      />
      {hint && <p className="text-[10px] text-[var(--sage-text-muted)] mt-1">{hint}</p>}
    </div>
  ),
);
TextInput.displayName = 'TextInput';

/* ───────── Textarea ───────── */
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  /** Use mono font (for code/system instructions) */
  mono?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, hint, mono, className = '', ...rest }, ref) => (
    <div className="space-y-1">
      {label && (
        <label className="block text-[10px] font-bold text-[var(--sage-text-muted)] uppercase tracking-[0.15em]">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        className={`${baseInputClass} p-2.5 text-sm resize-none ${mono ? 'font-[var(--sage-font-mono)]' : ''} ${className}`}
        {...rest}
      />
      {hint && <p className="text-[10px] text-[var(--sage-text-muted)] mt-1">{hint}</p>}
    </div>
  ),
);
Textarea.displayName = 'Textarea';

/* ───────── Select ───────── */
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: Array<{ value: string; label: string }>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, className = '', ...rest }, ref) => (
    <div className="space-y-1">
      {label && (
        <label className="block text-[10px] font-bold text-[var(--sage-text-muted)] uppercase tracking-[0.15em]">
          {label}
        </label>
      )}
      <select
        ref={ref}
        className={`${baseInputClass} p-2 text-sm ${className}`}
        {...rest}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  ),
);
Select.displayName = 'Select';
